import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Box, Input, LoaderIndicator } from "..";
import api from "../../../api/api";
import { uploadFile as uploadFileFallbackFn } from "../../../api/uploads";
import checkIcon from "../../../assets/icons/checkmark_green.svg";
import imageIcon from "../../../assets/icons/image.svg";
import { getFormErrorMsg } from "../../../helpers/formErrorMessages";
import "./FileUpload.scss";
import { Image } from "../image";

const fileLinkStyle = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
  color: "#203864",
  textDecoration: "underline",
  cursor: "pointer",
};

const FileNameLink = ({ href, downloadName, children, style = {} }) => (
  <a
    href={href}
    download={downloadName}
    target="_blank"
    rel="noopener noreferrer"
    style={{ ...fileLinkStyle, ...style }}
  >
    {children}
  </a>
);

const getUploadId = (upload) => {
  if (!upload) return null;
  if (upload.id) return upload.id;
  if (typeof upload === "string") {
    const match = upload.match(/\/uploads\/(\d+)/);
    return match ? match[1] : null;
  }
  if (upload["@id"]) return getUploadId(upload["@id"]);
  return null;
};

const FileUploadField = ({
  id,
  name,
  type,
  fileUploadType,
  onSuccess,
  allowedTypes,
  title,
  uploadLabel,
  isReadonly = false,
  defaultValue = null,
  uploadUrl,
  resetStateOnSuccess = false,
  uploadFile = uploadFileFallbackFn,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState();
  const [selectedFile, setSelectedFile] = useState();
  const [localFile, setLocalFile] = useState(null);
  const [localFileUrl, setLocalFileUrl] = useState(null);
  const [serverUpload, setServerUpload] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const {
    register,
    formState: { errors },
    setValue,
    clearErrors,
    setError,
  } = useFormContext();
  const registeredField = register(name);

  const handleFileUpload = (file) => {
    setLoading(true);

    uploadFile({ file, type: fileUploadType })
      .then((res) => {
        enqueueSnackbar("File successfully uploaded", {
          variant: "success",
        });

        clearErrors(name);
        setLoading(false);

        if (onSuccess) {
          onSuccess(res);
        }

        if (resetStateOnSuccess) {
          setSelectedFile();
          setDescription();
          setLocalFile(null);
          setValue(name, undefined);
        } else {
          setValue(name, res.data["@id"]);
        }
      })
      .catch(() => {
        setLoading(false);
        setValue(name, undefined);
        setLocalFile(null);
        setSelectedFile();
        setDescription();
        setError(name, { type: "custom", message: "Error uploading file. Please try again." });
        enqueueSnackbar("Error uploading file", {
          variant: "error",
        });
      });
  };

  useEffect(() => {
    if (!localFile) {
      setLocalFileUrl(null);
      return undefined;
    }

    const url = URL.createObjectURL(localFile);
    setLocalFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [localFile]);

  useEffect(() => {
    if (defaultValue) {
      setLocalFile(null);
      setServerUpload(defaultValue);
      setSelectedFile(defaultValue.filename || defaultValue.fileOriginalName);
      setDescription(defaultValue.fileOriginalName || defaultValue.filename);
    } else {
      setServerUpload(null);
    }
  }, [defaultValue]);

  const handleServerDownload = async (event) => {
    event.preventDefault();
    const uploadId = getUploadId(serverUpload);
    if (!uploadId || downloading) return;

    setDownloading(true);
    try {
      const response = await api.get(`/uploads/${uploadId}/download`, {
        responseType: "blob",
        skipGlobalErrorToast: true,
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = description || "download";
      link.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      enqueueSnackbar("Error downloading file", { variant: "error" });
    } finally {
      setDownloading(false);
    }
  };

  const renderFileName = (style = {}) => {
    if (localFileUrl) {
      return (
        <FileNameLink
          href={localFileUrl}
          downloadName={description}
          style={style}
        >
          {description}
        </FileNameLink>
      );
    }

    if (isReadonly && getUploadId(serverUpload)) {
      return (
        <a
          href={`/uploads/${getUploadId(serverUpload)}/download`}
          onClick={handleServerDownload}
          style={{
            ...fileLinkStyle,
            ...style,
            opacity: downloading ? 0.6 : 1,
            pointerEvents: downloading ? "none" : "auto",
          }}
        >
          {description}
        </a>
      );
    }

    return (
      <span style={{ ...style, wordBreak: "break-word", overflowWrap: "break-word" }}>
        {description}
      </span>
    );
  };

  const hasUploadedFile = Boolean(selectedFile) || (isReadonly && serverUpload);

  return (
    <div
      className={`file-upload${isReadonly ? " file-upload--readonly" : ""}`}
      style={{ width: '100%', maxWidth: '100%' }}
    >
      <div
        style={{
          fontSize: "1.6rem",
          fontWeight: 500,
          paddingLeft: "2.5rem",
          marginBottom: "1rem",
          wordBreak: "break-word",
          overflowWrap: "break-word"
        }}
      >
        {title}
      </div>
      <Box
        className="flex-column align-center justify-center outlined dotted"
        gap={2}
        style={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}
      >
        {loading ? (
          <LoaderIndicator />
        ) : !hasUploadedFile && isReadonly ? (
          <div
            className="flex-column align-center justify-center file-upload__empty-readonly"
            style={{ width: "100%", padding: "0 1rem" }}
          >
            <div style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>{uploadLabel}</div>
            <div style={{ marginTop: "1rem", fontSize: "1.4rem" }}>No file uploaded</div>
          </div>
        ) : !hasUploadedFile ? (
          <div className="flex-column align-center justify-center" style={{ width: '100%', padding: '0 1rem' }}>
            <div style={{ textAlign: "center", wordBreak: "break-word", overflowWrap: "break-word" }}>{uploadLabel}</div>
            <Input
              {...registeredField}
              type="file"
              accept={allowedTypes.join(", ")}
              label={"Click here to upload"}
              onChange={(event) => {
                const file = event?.target?.files?.[0];
                if (!file) return;

                if (file.size >= 100000000) {
                  setDescription("");
                  setError(name, {
                    type: "custom",
                    message: "File size must be smaller than 100MB",
                  });
                  return;
                }

                clearErrors(name);
                setLocalFile(file);
                setSelectedFile(file);
                setDescription(file.name);
                handleFileUpload(file);
              }}
            />
          </div>
        ) : (
          <div className="flex-column align-center justify-center" style={{ width: '100%', padding: '0 1rem' }}>
            <div
              className="flex-column align-center justify-center"
              style={{
                border: "6px solid #4DCA22",
                height: 70,
                width: 70,
                borderRadius: "100px",
                padding: "1rem",
              }}
            >
              <Image src={checkIcon} alt="Check Icon" />
              <div
                style={{ color: "#4DCA22", fontWeight: 500, fontSize: "16px" }}
              >
                Done
              </div>
            </div>
            <div style={{ marginTop: "2rem", textAlign: "center" }}>
              {renderFileName()}
            </div>
          </div>
        )}
      </Box>
      <div style={{ marginLeft: "2rem", marginTop: "1rem", color: "#B7C0DF", wordBreak: "break-word", overflowWrap: "break-word" }}>
        Supported formats: {allowedTypes.join(", ")}
      </div>
      {selectedFile && description && !resetStateOnSuccess && (
        <div className="flex justify-between align-center upload-container" style={{ width: '100%', maxWidth: '100%' }}>
          <div className="flex justify-center align-center" style={{ flex: 1, minWidth: 0 }}>
            <Image src={imageIcon} alt="Image Icon" />
            <div style={{ marginLeft: "1rem", flex: 1, minWidth: 0 }}>
              {renderFileName()}
            </div>
          </div>
          {!isReadonly && (
            <div
              style={{ cursor: "pointer", flexShrink: 0 }}
              onClick={() => {
                setValue(name, undefined);
                setError(name, { type: "custom", message: "This document is required" });
                setSelectedFile();
                setDescription();
                setLocalFile(null);
                setServerUpload(null);
              }}
            >
              Remove
            </div>
          )}
        </div>
      )}
      {Boolean(getFormErrorMsg(errors, name)) && (
        <div
          className="error-hint"
          style={{ color: "red", marginLeft: "2rem", wordBreak: "break-word", overflowWrap: "break-word" }}
        >
          {getFormErrorMsg(errors, name)}
        </div>
      )}
    </div>
  );
};

export default FileUploadField;
